import { forwardRef, useRef, useState } from 'react';
import { Box, Button, type ButtonProps } from '@doist/marketist';
import { useGSAP } from '@gsap/react';
import cn from 'clsx';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import css from './big-button.module.css';
import type { OmitKnown } from 'types';
gsap.registerPlugin(ScrollTrigger);

/**
 * Static reflection effect
 */
function Glare({
  className
}: {
  className?: string;
}) {
  return <svg className={className} viewBox="0 0 166 65" fill="none" xmlns="http://www.w3.org/2000/svg" data-sentry-element="svg" data-sentry-component="Glare" data-sentry-source-file="big-button.tsx">
            <path d="M49 65H0V1h166C148 24 85 34 49 65Z" fill="currentColor" data-sentry-element="path" data-sentry-source-file="big-button.tsx" />
        </svg>;
}

/**
 * Animated reflection
 */
const Shine = forwardRef<SVGSVGElement>(function Shine(props, ref) {
  return <svg className={css.shine} viewBox="0 0 218 84" fill="none" xmlns="http://www.w3.org/2000/svg" {...props} ref={ref}>
            <path d="M129 8C111 31 48 42 12 72h81c36-30 99-41 117-64h-81Z" fill="#EE6449" />
        </svg>;
});

/**
 * Growing circle for an on-hover state
 */
function Circle() {
  return <Box className={css.circle} data-sentry-element="Box" data-sentry-component="Circle" data-sentry-source-file="big-button.tsx" />;
}

/**
 * Big Button, a more fun and branded design for the Start for Free buttons.
 *
 * @see:
 * - https://twist.com/a/1585/ch/1293/t/6167483/
 * - https://www.figma.com/design/HFcketAgkdeUIv6WHZ1dQw/Brand-%E2%80%93%C2%A0Todoist-Design-System?node-id=8492-32142&t=nTfEVuUc5wLVj3CU-11
 * - https://codepen.io/stephenbarkan/pen/bGyGPRg?editors=1100
 */
export function BigButton({
  className,
  ...props
}: OmitKnown<ButtonProps, 'variant' | 'size' | 'slotLayout'>) {
  const $box = useRef<HTMLDivElement>(null);
  const $shine = useRef<SVGSVGElement>(null);
  const [hovered, setHovered] = useState(false);

  /**
   * When BigButton enters viewport, we'll display a 'shine' animation,
   * to draw attention to the button.
   *
   * The animation is played 1s after entering viewport, unless someone
   * hovers the button during the countdown.
   */
  useGSAP((_, contextSafe) => {
    if (contextSafe) {
      const shine = contextSafe(() => {
        if (!hovered) {
          gsap.fromTo($shine.current, {
            xPercent: -100,
            x: 0
          }, {
            xPercent: 0,
            x: () => $box.current?.clientWidth ?? 0,
            duration: 0.65,
            ease: 'power1.in'
          });
        }
      });
      let timeout: NodeJS.Timeout | undefined;
      ScrollTrigger.create({
        trigger: $box.current,
        onEnter: () => {
          timeout = setTimeout(shine, 1000);
        },
        once: true
      });
      return () => {
        clearTimeout(timeout);
      };
    }
  }, {
    scope: $box,
    dependencies: [hovered, $box, $shine]
  });
  return <Box className={css.buttonWrapper} ref={$box} data-sentry-element="Box" data-sentry-component="BigButton" data-sentry-source-file="big-button.tsx">
            <Button variant="primary" size="xl" className={cn(className, css.bigButton)} slotLayout={<>
                        <Circle />
                        <Glare className={css.glareLeft} />
                        <Glare className={css.glareRight} />
                        <Shine ref={$shine} />
                    </>} onMouseEnter={() => hovered === false ? setHovered(true) : null} {...props} data-sentry-element="Button" data-sentry-source-file="big-button.tsx">
                {props.children}
            </Button>
            <Box className={css.shadow} data-sentry-element="Box" data-sentry-source-file="big-button.tsx" />
        </Box>;
}